import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const DelaySchema = Yup.object().shape({
  delaySeconds: Yup.number()
    .min(1, "Mínimo 1 segundo")
    .max(3600, "Máximo 1 hora (3600 segundos)")
    .required("Digite o tempo de espera"),
  enableTimeout: Yup.boolean(),
  timeoutSeconds: Yup.number().when("enableTimeout", {
    is: true,
    then: (schema) =>
      schema
        .min(1, "Mínimo 1 segundo")
        .max(3600, "Máximo 1 hora")
        .required("Digite o timeout"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const FlowBuilderDelayModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    delaySeconds: 5,
    enableTimeout: false,
    timeoutSeconds: 30,
  };

  const [activeModal, setActiveModal] = useState(false);
  const [labels, setLabels] = useState({
    title: "Adicionar Aguardar",
    btn: "Adicionar",
  });

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Aguardar",
        btn: "Salvar",
      });
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Aguardar",
        btn: "Adicionar",
      });
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = (values) => {
    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: values,
      });
    } else if (open === "create") {
      handleClose();
      onSave(values);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Formik
          initialValues={data?.data || initialState}
          enableReinitialize={true}
          validationSchema={DelaySchema}
          onSubmit={handleSave}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid style={{ width: "100%", marginTop: 40 }} container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>Tempo de Espera (segundos)</Typography>
                    <Field
                      as={TextField}
                      name="delaySeconds"
                      type="number"
                      error={touched.delaySeconds && Boolean(errors.delaySeconds)}
                      helperText={touched.delaySeconds && errors.delaySeconds || "Tempo em segundos (1-3600)"}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      placeholder="Ex: 5"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.enableTimeout}
                          onChange={(e) =>
                            setFieldValue("enableTimeout", e.target.checked)
                          }
                        />
                      }
                      label="Ativar Timeout (aguardar resposta do usuário com via alternativa)"
                    />
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
                      ℹ️ Sem timeout: apenas aguarda e continua. Com timeout: aguarda resposta do usuário e pode ir para via alternativa se não responder.
                    </Typography>
                  </Grid>

                  {values.enableTimeout && (
                    <Grid item xs={12}>
                      <Typography>Timeout (segundos)</Typography>
                      <Field
                        as={TextField}
                        name="timeoutSeconds"
                        type="number"
                        error={touched.timeoutSeconds && Boolean(errors.timeoutSeconds)}
                        helperText={touched.timeoutSeconds && errors.timeoutSeconds || "Tempo máximo de espera por resposta"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        placeholder="Ex: 30"
                      />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderDelayModal;
